import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InteractiveElement } from "@iventis/styles/src/components";
import { Content } from "@iventis/translations/content/typed-content";
import React, { FunctionComponent, ReactNode, useMemo } from "react";
import { borderRadius, styled } from "@iventis/styles";
import { LoadingComponent, ReadonlyToolTipComponent, SimpleTooltip } from "@iventis/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";

export type PanelButtonStyleProp = {
    buttonSize: string;
    iconSize: string;
    hoverColour: string;
    disabledColour: string;
    selectedColour: string;
    activeColour: string;
};

export const PanelButtonTemplate: FunctionComponent<{
    icon: ReactNode;
    text: string | ReactNode;
    action: () => void;
    style: PanelButtonStyleProp;
    selected?: boolean;
    disabled?: boolean;
    className?: string;
    loading?: boolean;
    dataCy?: string;
}> = ({ icon, text, action, style, selected, disabled = false, className = undefined, loading = false, dataCy }) => (
    <SimpleTooltip text={text} placement="left">
        <span>
            <StyledBackgroundHighlightButton className={className} selected={selected} style={style} onClick={action} disabled={disabled} data-cy={dataCy}>
                <ButtonContents style={style} className="Button-Contents">
                    <span className="hidden">{text}</span>
                    {loading && <LoadingComponent size={30} />}
                    {!loading && icon}
                </ButtonContents>
            </StyledBackgroundHighlightButton>
        </span>
    </SimpleTooltip>
);

export const CommentPanelButton = ({ action, style, selected, disabled, text, icon }) => (
    <StyledCommentPanelButton text={text} action={action} icon={icon} selected={selected} style={style} disabled={disabled} dataCy="toolbar__comments" />
);

export const InfoPanelButton = ({ action, selected, style }) => (
    <PanelButtonTemplate
        style={style}
        text=""
        action={action}
        icon={selected ? <FontAwesomeIcon size="lg" icon={["fas", "circle-info"]} /> : <FontAwesomeIcon size="lg" icon={["far", "circle-info"]} />}
        selected={selected}
        disabled={false}
    />
);

export const PencilPanelButton = ({
    action,
    style,
    selected,
    disabled,
    reasonForDisabled,
}: {
    action: () => void;
    style: PanelButtonStyleProp;
    selected: boolean;
    disabled: boolean;
    reasonForDisabled?: "permission" | "locked" | "hidden";
}) => {
    const translate = useIventisTranslate();

    const text = useMemo(() => {
        if (disabled && reasonForDisabled === "permission") {
            return <ReadonlyToolTipComponent resource="layer" />;
        }
        if (disabled && reasonForDisabled === "hidden") {
            return translate(Content.map5.verticalBarErrors.layer_hidden_drawing);
        }
        if (disabled && reasonForDisabled === "locked") {
            return translate(Content.map5.verticalBarErrors.layer_locked_drawing);
        }
        return translate(Content.map2.controls.draw);
    }, [translate, disabled, reasonForDisabled]);

    return (
        <PanelButtonTemplate
            dataCy="toolbar__draw-edit"
            style={style}
            text={text}
            action={action}
            selected={selected}
            icon={selected ? <FontAwesomeIcon size="lg" icon={["fas", "pencil"]} /> : <FontAwesomeIcon size="lg" icon={["far", "pencil"]} />}
            disabled={disabled}
        />
    );
};

export const VectorSquarePanelButton = ({ action, style, selected, disabled }) => {
    const translate = useIventisTranslate();
    return (
        <StyledVectorSquarePanelButtonTemplate
            dataCy="toolbar__select-area"
            style={style}
            text={translate(Content.map2.controls.select)}
            action={action}
            selected={selected}
            icon={
                <span className="fa-layers fa-fw">
                    <FontAwesomeIcon size="lg" icon={["far", "vector-square"]} />
                    <StyledFontAwesomeIcon $selected={selected} size="lg" icon={["fas", "arrow-pointer"]} transform={{ x: 4, y: 5 }} />
                </span>
            }
            disabled={disabled}
        />
    );
};

export const AreaSelectButton = ({ action, style, selected, disabled }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.area_select)}
            action={action}
            selected={selected}
            disabled={disabled}
            icon={
                <span className="fa-layers fa-fw">
                    <FontAwesomeIcon size="lg" icon={["far", "vector-square"]} />
                    <FontAwesomeIcon size="sm" icon={["fas", "layer-group"]} transform={{ x: 8, y: 8 }} />
                </span>
            }
        />
    );
};

export const AnalysisPanelButton = ({ action, style, disabled, selected }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            dataCy="toolbar__analysis"
            style={style}
            text={translate(Content.map2.controls.analysis)}
            action={action}
            selected={selected}
            disabled={disabled}
            icon={<FontAwesomeIcon size="lg" icon={["far", "ruler"]} />}
        />
    );
};

export const SearchPanelButton = ({ action, style, selected }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.search)}
            action={action}
            selected={selected}
            icon={<FontAwesomeIcon size="lg" icon={["far", "magnifying-glass"]} />}
        />
    );
};

export const GrabPanelButton = ({ action, style, selected }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.grab)}
            action={action}
            selected={selected}
            icon={<FontAwesomeIcon size="lg" icon={["far", "expand"]} />}
        />
    );
};

export const Terrain3DPanelButton = ({ action, style, selected }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.toggle_3d_terrain)}
            action={action}
            selected={selected}
            icon={<FontAwesomeIcon size="lg" icon={["far", "mountain"]} />}
        />
    );
};

export const GlobePanelButton = ({ action, style, selected }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.toggle_globe)}
            action={action}
            selected={selected}
            icon={<FontAwesomeIcon size="lg" icon={["far", "earth-europe"]} />}
        />
    );
};

export const ZoomInPanelButton = ({ action, style, selected, disabled }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.zoom_in)}
            action={action}
            selected={selected}
            icon={<FontAwesomeIcon size="lg" icon={["far", "magnifying-glass-plus"]} />}
            disabled={disabled}
            dataCy="map-zoom-in-button"
        />
    );
};

export const ZoomOutPanelButton = ({ action, style, selected, disabled }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.zoom_out)}
            action={action}
            selected={selected}
            icon={<FontAwesomeIcon size="lg" icon={["far", "magnifying-glass-minus"]} />}
            disabled={disabled}
            dataCy="map-zoom-out-button"
        />
    );
};

export const SortPanelButton = ({ action, style, selected, mapPosition }) => {
    // Get our current position from the engine

    /** The amount we want to dampen our vertical rotation of the compass icon. */
    const rotationDampen = 0.75;
    const rotationX = mapPosition.pitch * rotationDampen;
    const rotationZ = mapPosition.bearing * -1; // Rotate in the inverse so it always points north

    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.reset)}
            action={action}
            selected={selected}
            icon={<FontAwesomeIcon size="lg" icon={["fad", "sort"]} style={{ transform: `rotateX(${rotationX}deg) rotateZ(${rotationZ}deg)` }} />}
        />
    );
};

export const VrPanelButton = ({ action, style, selected, isConnecting }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={`${translate(Content.map2.controls.vr)}`}
            action={action}
            icon={<FontAwesomeIcon size="lg" icon={["fas", "aperture"]} />}
            selected={selected}
            dataCy="toolbar__vr"
            loading={isConnecting}
        />
    );
};

export const BookmarksButton = ({ action, style, selected, disabled }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.bookmarks)}
            action={action}
            selected={selected}
            icon={<FontAwesomeIcon size="lg" icon={["fas", "bookmark"]} />}
            disabled={disabled}
        />
    );
};

export const FocusOnObjectsButton = ({ action, style, selected, disabled, loading }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map2.controls.zoom_to_all_objects)}
            action={action}
            icon={
                <span className="fa-layers fa-fw">
                    <FontAwesomeIcon size="2x" icon={["fal", "expand"]} transform={{ x: -2, y: 0 }} />
                    <FontAwesomeIcon size="sm" icon={["far", "cube"]} />
                </span>
            }
            selected={selected}
            disabled={disabled}
            loading={loading}
        />
    );
};

export const AngleofViewButton = ({ action, style, isPitched }) => {
    const translate = useIventisTranslate();
    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map5.controls.set_pitch)}
            action={action}
            icon={<StyledAngleIcon>{isPitched ? <span>2D</span> : <span>3D</span>}</StyledAngleIcon>}
        />
    );
};
export const UserLocationButton = ({ action, style }) => {
    const translate = useIventisTranslate();

    return (
        <PanelButtonTemplate
            dataCy="user__location"
            style={style}
            text={translate(Content.map8.controls.user_location)}
            action={action}
            icon={<FontAwesomeIcon size="lg" icon={["far", "location-crosshairs"]} />}
        />
    );
};

export const LockPanelButton = ({ action, selected, style }) => {
    const translate = useIventisTranslate();

    return (
        <PanelButtonTemplate
            style={style}
            text={translate(Content.map5.export_map.lock_map)}
            action={action}
            icon={selected ? <FontAwesomeIcon size="lg" icon={["fas", "lock"]} /> : <FontAwesomeIcon size="lg" icon={["fas", "unlock"]} />}
            selected={false}
            disabled={false}
        />
    );
};

const StyledBackgroundHighlightButton = styled(InteractiveElement)<{ style: PanelButtonStyleProp; selected: boolean }>`
    position: relative;
    z-index: 200;
    width: ${({ style }: { style: PanelButtonStyleProp }) => style.buttonSize};
    height: ${({ style }: { style: PanelButtonStyleProp }) => style.buttonSize};
    display: flex;
    justify-content: center;
    align-items: center;
    ${(props) => (props.disabled ? "pointer-events: none" : "")};
    cursor: ${(props) => (props.disabled ? "default" : "pointer")};

    .Button-Contents {
        border-radius: ${borderRadius.input};
        background-color: ${(props) => (props.selected ? props.style.selectedColour : null)};
        color: ${(props) => (props.disabled ? props.style.disabledColour : null)};
    }
    :hover {
        .Button-Contents {
            background-color: ${(props) => (props.disabled ? null : props.selected ? props.style.selectedColour : props.style.hoverColour)};
        }
    }

    :active {
        .Button-Contents {
            color: ${(props) => props.style.activeColour};
        }
    }
`;

const ButtonContents = styled.span<{ style: PanelButtonStyleProp }>`
    width: ${({ style }: { style: PanelButtonStyleProp }) => style.iconSize};
    height: ${({ style }: { style: PanelButtonStyleProp }) => style.iconSize};
    display: flex;
    justify-content: center;
    align-items: center;
    margin: auto;
`;

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)<{ $selected: boolean }>`
    g g path {
        stroke: ${(props) => (props.$selected ? props.theme.primaryColors.focus : props.theme.primaryColors.subdued70)};
        // not sure why we need such a high number for the stroke-width
        stroke-width: 100px;
        // Ensures the fill pains over the stroke
        paint-order: stroke;
    }
`;

const StyledVectorSquarePanelButtonTemplate = styled(PanelButtonTemplate)`
    :hover {
        g g path {
            stroke: ${(props) => props.theme.typographyColors.lessSubdued};
        }
    }
`;

const StyledAngleIcon = styled.span`
    font-weight: 500;
    font-size: 19px;
`;

const StyledCommentPanelButton = styled(PanelButtonTemplate)`
    position: fixed;
    top: 108px;
    right: 14px;
    width: 44px;
    height: 44px;
    border-radius: ${borderRadius.input};
    background-color: ${(props) => (!props.selected ? props.theme.primaryColors.focus : props.theme.primaryColors.subdued70)};
    box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.3);
    .Button-Contents {
        background-color: transparent !important;
    }
`;
